/**
 * Org-scoped AI client insights for a single client.
 * Stored at: aiProfiles/{orgId}/{userId}
 */
export const CLIENT_AI_PROFILE_VERSION = 3;

export interface ClientAiProfileSnapshot {
  /** Short label for where the client is in coaching (e.g. "fat loss phase 2") */
  currentPhase: string;
  /** Qualitative adherence read (e.g. "strong / uneven / low") */
  adherence: string;
  /** Direction of recent progress (e.g. "improving / stalled / regressing") */
  trend: string;
  /** One-line goals summary */
  goalsSummary: string;
}

export interface ClientAiProfileProgramContext {
  nutrition: string;
  training: string;
  cardio: string;
  supplements: string;
}

export interface ClientAiProfileSources {
  checkInIds: string[];
  noteIds: string[];
  programIds: string[];
  goalIds: string[];
  formIds: string[];
  habitIds: string[];
  conversationIds: string[];
  messageIds: string[];
}

/** Most recent notable client event for banner / modal headline */
export interface ClientAiProfileLatestUpdate {
  text: string;
  /** ISO timestamp of the underlying event (check-in, note, chat, etc.) — not brief generation time */
  at?: string;
}

export interface ClientAiProfile {
  version: number;
  orgId: string;
  userId: string;
  generatedAt: string;
  model?: string;
  /** 2–4 sentence narrative overview for the trainer */
  summary: string;
  snapshot: ClientAiProfileSnapshot;
  strengths: string[];
  riskFlags: string[];
  /** Known injuries, limitations, or recovery constraints */
  injuries: string[];
  programContext: ClientAiProfileProgramContext;
  recentChanges: string[];
  openQuestions: string[];
  suggestedFocus: string[];
  sources: ClientAiProfileSources;
  /** Headline update with event timestamp when known */
  latestSignificantUpdate?: ClientAiProfileLatestUpdate;
}

export function emptyClientAiProfileSources(): ClientAiProfileSources {
  return {
    checkInIds: [],
    noteIds: [],
    programIds: [],
    goalIds: [],
    formIds: [],
    habitIds: [],
    conversationIds: [],
    messageIds: [],
  };
}

function isLowSignalUpdate(text: string): boolean {
  const value = (text || '').trim().toLowerCase();
  if (!value || value.length < 24) {
    return true;
  }
  return (
    /^check-in\b/.test(value) ||
    /^check in\b/.test(value) ||
    /submitted (a )?check[- ]?in/.test(value) ||
    /latest check[- ]?in on/.test(value) ||
    /no recent measurable/.test(value) ||
    /no recent /.test(value) ||
    /team call/.test(value) ||
    /^client:\s/.test(value) ||
    /^coach:\s/.test(value) ||
    /^form submitted/.test(value) ||
    /^form:\s/.test(value)
  );
}

/** Headline for banner / modal top: most recent significant update when available. */
export function getAiProfileLatestUpdate(
  profile: ClientAiProfile | null | undefined
): { text: string; at: string | null } | null {
  if (!profile) {
    return null;
  }

  const structuredText = profile.latestSignificantUpdate?.text?.trim();
  if (structuredText && !isLowSignalUpdate(structuredText)) {
    return {
      text: structuredText,
      at: profile.latestSignificantUpdate?.at || null,
    };
  }

  const text = profile.recentChanges?.[0]?.trim();
  if (!text || isLowSignalUpdate(text)) {
    return null;
  }
  return {
    text,
    at: null,
  };
}
